import { NavLink } from 'react-router-dom'
import type { User } from 'oidc-client-ts'
import {
  BarChart3,
  CircleDollarSign,
  Compass,
  FileClock,
  LayoutGrid,
  LogOut,
  Sparkles,
  Users as UsersIcon,
  Wand2,
} from 'lucide-react'
import { signOut } from '@/lib/auth'
import { Button } from './ui/button'
import { cn } from '@/lib/utils'

const NAV = [
  { to: '/', label: 'Dashboard', icon: BarChart3, end: true },
  { to: '/users', label: 'Users', icon: UsersIcon, end: false },
  { to: '/jobs', label: 'Generations', icon: Wand2, end: false },
  { to: '/costs', label: 'Costs', icon: CircleDollarSign, end: false },
  { to: '/catalog', label: 'Catalogue', icon: LayoutGrid, end: false },
  { to: '/discover', label: 'Discover', icon: Compass, end: false },
  { to: '/audit', label: 'Audit log', icon: FileClock, end: false },
] as const

export function AppLayout({ user, children }: { user: User; children: React.ReactNode }) {
  const email = user.profile.email ?? user.profile.preferred_username ?? 'admin'

  return (
    <div className="flex min-h-screen">
      <aside className="flex w-56 shrink-0 flex-col border-r bg-[var(--color-surface)]">
        <div className="flex items-center gap-2 px-5 py-5">
          <Sparkles className="size-5 text-[var(--color-accent-soft)]" />
          <span className="font-semibold">Pixviro</span>
        </div>

        <nav className="flex-1 space-y-1 px-3">
          {NAV.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors',
                  isActive
                    ? 'bg-[var(--color-accent)]/15 text-[var(--color-accent-soft)]'
                    : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-elevated)]',
                )
              }
            >
              <Icon className="size-4" />
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="space-y-2 border-t p-3">
          <p className="truncate px-2 text-xs text-[var(--color-text-tertiary)]" title={email}>
            {email}
          </p>
          <Button variant="ghost" size="sm" className="w-full justify-start" onClick={() => void signOut()}>
            <LogOut className="size-4" />
            Sign out
          </Button>
        </div>
      </aside>

      <main className="flex-1 overflow-x-hidden">{children}</main>
    </div>
  )
}

export function PageHeader({
  title,
  description,
  actions,
}: {
  title: string
  description?: string
  actions?: React.ReactNode
}) {
  return (
    <div className="flex items-start justify-between gap-4 border-b px-8 py-6">
      <div>
        <h1 className="text-xl font-semibold">{title}</h1>
        {description && <p className="mt-1 text-sm text-[var(--color-text-secondary)]">{description}</p>}
      </div>
      {actions && <div className="flex gap-2">{actions}</div>}
    </div>
  )
}
